import { STYLES, REFERENCES, NS, INPUT, KEYDOWN, FOCUS, BLUR, LINE_MODE } from "../constants";
import type { IPromptBoxOptions, IPromptBoxMessages } from "../models";
import type { IKendoJQuery } from "../../core";

declare const $: any;

const DEFAULT_MESSAGES: IPromptBoxMessages = {
    placeholder: "",
    actionButton: "Send prompt",
    actionButtonLoading: "Stop generating",
    messageBoxTitle: "Prompt input"
};

export interface IInputManagerCallbacks {
    onInput: () => void;
    onKeyDown: (e: any) => void;
    onFocus: () => void;
    onBlur: () => void;
    onMultilineStateChange: (isMultiline: boolean, wasMultiline: boolean) => void;
}

export interface IInputManagerContext {
    wrapper: IKendoJQuery;
    getOptions: () => IPromptBoxOptions;
    isPlaceholderExplicit?: () => boolean;
    getMode: () => string;
    callbacks: IInputManagerCallbacks;
}

export class InputManager {
    private context: IInputManagerContext;
    private inputInstance: any = null;
    private singleRowHeight: number = 0;
    private singleLineWidth: number = 0;
    private initialHeight: number = 0;
    private minMultiRowHeight: number = 0;
    private multiline: boolean = false;

    constructor(context: IInputManagerContext) {
        this.context = context;
    }

    init(): void {
        const inputContainer = this.context.wrapper.find(`[${REFERENCES.input}]`);
        const mode = this.context.getMode();
        const inputElement = this.createInputElement(mode);
        inputContainer.replaceWith(inputElement);

        const value = this.context.getOptions().value || "";
        if (value) {
            inputElement.val(value);
        }

        this.captureInitialDimensions();
        this.attachInputEvents();
    }

    getElement(): JQuery | null {
        return this.inputInstance?.element || null;
    }

    getValue(): string {
        return this.inputInstance ? this.inputInstance.value() : "";
    }

    setValue(value: string): void {
        if (this.inputInstance) {
            this.inputInstance.value(value);
        }
        this.updateAutoResize();
        this.updateMultiResize();
    }

    enable(enabled: boolean): void {
        if (this.inputInstance) {
            this.inputInstance.enable(enabled);
        }
    }

    setReadonly(readonly: boolean): void {
        if (this.inputInstance?.element) {
            this.inputInstance.element.prop("readonly", readonly);
        }
    }

    focus(): void {
        if (this.inputInstance) {
            this.inputInstance.focus();
        }
    }

    blur(): void {
        if (this.inputInstance?.element) {
            this.inputInstance.element.blur();
        }
    }

    isMultiline(): boolean {
        return this.multiline;
    }

    updateAutoResize(): void {
        const mode = this.context.getMode();

        if (mode !== LINE_MODE.AUTO || !this.inputInstance) {
            return;
        }

        const textarea = this.inputInstance.element;
        if (!textarea.is("textarea")) {
            return;
        }

        const el = textarea[0] as HTMLTextAreaElement;

        if (el.offsetHeight > 0 && this.initialHeight < el.offsetHeight) {
            this.captureInitialDimensions();
        }

        if (!this.multiline) {
            const needsExpansion = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;

            if (needsExpansion) {
                this.updateMultilineState(true);
                this.updateHeight();
            }
        } else {
            this.updateHeight();

            if (this.shouldCollapse()) {
                this.updateMultilineState(false);
                textarea.css("height", "");
            }
        }
    }

    updateMultiResize(): void {
        const mode = this.context.getMode();

        if (mode !== LINE_MODE.MULTI || !this.inputInstance) {
            return;
        }

        const textarea = this.inputInstance.element;
        if (!textarea.is("textarea")) {
            return;
        }

        const el = textarea[0];
        const options = this.context.getOptions();

        if (el.offsetHeight > 0 && this.initialHeight < el.offsetHeight) {
            this.captureInitialDimensions();
        }

        textarea.css("overflow", "hidden");
        textarea.css("height", this.initialHeight + "px");

        const scrollHeight = el.scrollHeight;
        const newHeight = this.calculateMultiHeight(scrollHeight, options.maxTextAreaHeight);

        if (newHeight === options.maxTextAreaHeight) {
            textarea.css("overflow", "");
        }

        textarea.css("height", newHeight + "px");
    }

    updatePlaceholder(): void {
        const options = this.context.getOptions();
        const messages = $.extend({}, DEFAULT_MESSAGES, options.messages);
        const placeholder = this.resolvePlaceholder(options, messages);

        if (this.inputInstance?.element) {
            this.inputInstance.element.attr("placeholder", placeholder);
        }
    }

    destroy(): void {
        if (this.inputInstance) {
            this.inputInstance.element.off(NS);
            this.inputInstance.destroy();
            this.inputInstance = null;
        }
    }

    private createInputElement(mode: string): JQuery {
        const options = this.context.getOptions();
        const messages = $.extend({}, DEFAULT_MESSAGES, options.messages);
        const title = options.title ? `title="${options.title}"` : "";
        const readonly = options.readonly ? "readonly" : "";
        const placeholder = this.resolvePlaceholder(options, messages);

        let inputElement: JQuery;

        if (mode === LINE_MODE.SINGLE) {
            inputElement = $(`<input type="text" class="${STYLES.promptBoxInput} ${STYLES.inputInner}" placeholder="${placeholder}" autocomplete="off" ${title} ${readonly} />`);

            this.inputInstance = {
                element: inputElement,
                value: (val?: string) => {
                    if (val === undefined) {
                        return inputElement.val() as string;
                    }
                    inputElement.val(val);
                },
                enable: (enabled: boolean) => {
                    inputElement.prop("disabled", !enabled);
                },
                focus: () => inputElement.trigger("focus"),
                destroy: () => { }
            };
        } else {
            const rows = mode === LINE_MODE.AUTO ? 1 : (options.rows || 1);
            inputElement = $(`<textarea class="${STYLES.promptBoxTextarea} ${STYLES.inputInner}" placeholder="${placeholder}" rows="${rows}" aria-multiline="true" ${title} ${readonly}></textarea>`);

            if (options.maxTextAreaHeight) {
                inputElement.css("max-height", options.maxTextAreaHeight + "px");
                inputElement.css("overflow-y", "auto");
            }

            this.inputInstance = {
                element: inputElement,
                value: (val?: string) => {
                    if (val === undefined) {
                        return inputElement.val() as string;
                    }
                    inputElement.val(val);
                },
                enable: (enabled: boolean) => {
                    inputElement.prop("disabled", !enabled);
                },
                focus: () => inputElement.trigger("focus"),
                destroy: () => { }
            };
        }

        return inputElement;
    }

    private resolvePlaceholder(options: IPromptBoxOptions, messages: IPromptBoxMessages): string {
        const isPlaceholderExplicit = this.context.isPlaceholderExplicit ? this.context.isPlaceholderExplicit() : options.placeholder !== undefined;

        if (isPlaceholderExplicit) {
            return options.placeholder ?? "";
        }

        return messages.placeholder;
    }

    private captureInitialDimensions(): void {
        const mode = this.context.getMode();

        if (mode === LINE_MODE.SINGLE || !this.inputInstance) {
            return;
        }

        const textarea = this.inputInstance.element;
        if (!textarea.is("textarea")) {
            return;
        }

        const el = textarea[0] as HTMLTextAreaElement;
        const computedStyle = window.getComputedStyle(el);
        const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
        const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
        const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

        const options = this.context.getOptions();
        const rows = options.rows || 1;

        this.singleRowHeight = lineHeight + paddingTop + paddingBottom;
        this.initialHeight = el.offsetHeight || this.singleRowHeight;
        this.singleLineWidth = el.offsetWidth;
        this.minMultiRowHeight = lineHeight * rows + paddingTop + paddingBottom;
    }

    private shouldCollapse(): boolean {
        if (!this.inputInstance || this.singleLineWidth <= 0) {
            return false;
        }

        const textarea = this.inputInstance.element as JQuery;
        if (!textarea.is("textarea")) {
            return false;
        }

        const el = textarea[0];
        const currentHeight = el.style.height;

        textarea.css({
            overflow: "hidden",
            width: this.singleLineWidth + "px",
            whiteSpace: "nowrap",
            height: this.initialHeight + "px"
        });

        const fitsSingleLine =
            el.scrollWidth <= this.singleLineWidth && el.scrollHeight <= this.initialHeight;

        textarea.css({
            overflow: "",
            width: "",
            whiteSpace: ""
        });

        if (currentHeight) {
            textarea.css("height", currentHeight);
        } else {
            textarea.css("height", "");
        }

        return fitsSingleLine;
    }

    private updateHeight(): void {
        const options = this.context.getOptions();

        if (!this.inputInstance) {
            return;
        }

        const textarea = this.inputInstance.element as JQuery;
        if (!textarea.is("textarea")) {
            return;
        }

        const el = textarea[0];

        textarea.css("overflow", "hidden");
        textarea.css("height", this.initialHeight + "px");

        const scrollHeight = el.scrollHeight;
        const newHeight = options.maxTextAreaHeight
            ? Math.min(scrollHeight, options.maxTextAreaHeight)
            : scrollHeight;

        if (newHeight === options.maxTextAreaHeight) {
            textarea.css("overflow", "");
        }

        textarea.css("height", newHeight + "px");
    }

    private calculateMultiHeight(scrollHeight: number, maxTextAreaHeight: number | undefined): number {
        if (maxTextAreaHeight && scrollHeight > maxTextAreaHeight) {
            return maxTextAreaHeight;
        }
        return Math.max(this.minMultiRowHeight, scrollHeight);
    }

    private updateMultilineState(isMultiline: boolean): void {
        const mode = this.context.getMode();

        if (mode !== LINE_MODE.AUTO) {
            return;
        }

        if (this.multiline === isMultiline) {
            return;
        }

        const wasMultiline = this.multiline;
        this.multiline = isMultiline;

        if (isMultiline) {
            this.context.wrapper.addClass(STYLES.promptBoxMultiline);
        } else {
            this.context.wrapper.removeClass(STYLES.promptBoxMultiline);
        }

        this.context.callbacks.onMultilineStateChange(isMultiline, wasMultiline);
    }

    private attachInputEvents(): void {
        if (!this.inputInstance) {
            return;
        }

        const inputElement = this.inputInstance.element as JQuery;

        inputElement.on(INPUT + NS, () => {
            this.updateAutoResize();
            this.updateMultiResize();
            this.context.callbacks.onInput();
        });

        inputElement.on(KEYDOWN + NS, (e: any) => {
            this.context.callbacks.onKeyDown(e);
        });

        inputElement.on(FOCUS + NS, () => {
            this.context.callbacks.onFocus();
        });

        inputElement.on(BLUR + NS, () => {
            this.context.callbacks.onBlur();
        });
    }
}
